import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import remarkGfm from 'remark-gfm';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { Message, Sender } from '../../types/tutor';
import { cn } from '../../lib/utils';
import { motion } from 'framer-motion';


interface ChatMessageBubbleProps {
    message: Message;
    isLatest?: boolean;
    onActionClick?: (text: string) => void;
}

const ChatMessageBubble: React.FC<ChatMessageBubbleProps> = React.memo(({ message, isLatest, onActionClick }) => {
    const isUser = message.sender === Sender.USER;

    // Parse out the pill suggestions
    const { cleanText, pills } = useMemo(() => {
        if (isUser) return { cleanText: message.text, pills: [] };

        // Normalize shared delimiters: |||PILL: A|||PILL: B||| → |||PILL: A||| |||PILL: B|||
        const normalized = message.text.replace(/\|\|\|(?=PILL:)/g, '||| |||');
        const pillRegex = /\|\|\|PILL:\s*(.*?)\s*\|\|\|/g;
        const matches = Array.from(normalized.matchAll(pillRegex));
        const extractedPills = Array.from(new Set(matches.map((m: any) => m[1])));

        // 1. Remove completely formed pills
        let cleanedText = normalized.replace(pillRegex, '').trim();

        // 2. Hide incomplete pills while streaming (so the user never sees `|||` artifacts)
        cleanedText = cleanedText.replace(/\|\|\|[\s\S]*/, '').trim();

        return { cleanText: cleanedText, pills: extractedPills };
    }, [message.text, isUser]);

    return (
        <div className={cn("flex w-full mb-8", isUser ? "justify-end" : "justify-start")}>
            <div className={cn("flex max-w-[95%] md:max-w-[95%] gap-3", isUser ? "flex-row-reverse" : "flex-row")}>



                {/* Message Bubble + Pills Container */}
                <div className="flex flex-col gap-3 min-w-0 flex-1">
                    <div className={cn(
                        "flex flex-col text-base md:text-lg break-words min-w-0 w-fit",
                        isUser
                            ? "p-4 rounded-2xl rounded-tr-none shadow-sm bg-primary text-primary-foreground ml-auto"
                            : "py-2 pr-4 pl-0 text-foreground" // Transparent background for bot, minimal padding
                    )}>

                        {/* Main Text Content */}
                        <div className="markdown-body w-full overflow-hidden">
                            <ReactMarkdown
                                remarkPlugins={[remarkMath, remarkGfm]}
                                rehypePlugins={[[rehypeKatex, { strict: false, trust: true }]]}
                                components={{
                                    // Enhance styling of markdown elements to match dashboard theme
                                    p: ({ node, ...props }) => (
                                        <p className="mb-4 last:mb-0 leading-loose text-base animate-in fade-in duration-300" {...props} />
                                    ),
                                    ul: ({ node, ...props }) => (
                                        <ul className="list-disc pl-5 mb-2 space-y-1 animate-in fade-in duration-300" {...props} />
                                    ),
                                    ol: ({ node, ...props }) => (
                                        <ol className="list-decimal pl-5 mb-2 space-y-1 animate-in fade-in duration-300" {...props} />
                                    ),
                                    li: ({ node, ...props }) => (
                                        <li className="pl-1" {...props} />
                                    ),
                                    h1: ({ node, ...props }) => (
                                        <h1 className="text-2xl font-bold mb-3 mt-6 first:mt-0 animate-in fade-in duration-300" {...props}>
                                            {props.children}
                                        </h1>
                                    ),
                                    h2: ({ node, ...props }) => (
                                        <h2 className="text-xl font-bold mb-2 mt-5 first:mt-0 animate-in fade-in duration-300" {...props}>
                                            {props.children}
                                        </h2>
                                    ),
                                    h3: ({ node, ...props }) => (
                                        <h3 className="text-lg font-semibold mb-2 mt-4 animate-in fade-in duration-300" {...props}>
                                            {props.children}
                                        </h3>
                                    ),
                                    code: ({ node, inline, className, children, ...props }: any) => {
                                        return !inline ? (
                                            <div
                                                className="bg-muted p-2 rounded-md my-2 overflow-x-auto text-xs font-mono border border-border animate-in fade-in duration-300"
                                            >
                                                <code className={className} {...props}>
                                                    {children}
                                                </code>
                                            </div>
                                        ) : (
                                            <code className="bg-muted px-1.5 py-0.5 rounded text-xs font-mono text-foreground" {...props}>
                                                {children}
                                            </code>
                                        );
                                    },
                                    blockquote: ({ node, ...props }) => (
                                        <blockquote
                                            className="border-l-4 border-primary/50 pl-4 py-1 italic bg-muted/30 rounded-r-md my-2 animate-in fade-in duration-300"
                                            {...props}
                                        />
                                    ),
                                    // Horizontal rule
                                    hr: ({ node, ...props }) => (
                                        <hr className="my-6 border-t-2 border-border/40 animate-in fade-in duration-300" {...props} />
                                    ),
                                    // Table components
                                    table: ({ node, ...props }) => (
                                        <div className="overflow-x-auto my-3 animate-in fade-in duration-300">
                                            <table className="min-w-full border-collapse border border-border rounded-md" {...props} />
                                        </div>
                                    ),
                                    thead: ({ node, ...props }) => (
                                        <thead className="bg-muted" {...props} />
                                    ),
                                    tbody: ({ node, ...props }) => (
                                        <tbody {...props} />
                                    ),
                                    tr: ({ node, ...props }) => (
                                        <tr className="border-b border-border hover:bg-muted/50 transition-colors" {...props} />
                                    ),
                                    th: ({ node, ...props }) => (
                                        <th className="px-4 py-2 text-left font-semibold border border-border" {...props} />
                                    ),
                                    td: ({ node, ...props }) => (
                                        <td className="px-4 py-2 border border-border" {...props} />
                                    )
                                }}
                            >
                                {cleanText}
                            </ReactMarkdown>
                        </div>
                    </div>

                    {/* Smart Chat Pills */}
                    {!isUser && isLatest && pills.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-1 px-1">
                            {pills.map((pillText, idx) => (
                                <motion.button
                                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    transition={{ delay: idx * 0.1, duration: 0.3 }}
                                    key={idx}
                                    onClick={() => onActionClick?.(pillText)}
                                    className="flex-shrink-0 bg-transparent dark:bg-neutral-800 border-2 border-b-[4px] border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-300 hover:border-brand-orange hover:text-brand-orange hover:bg-orange-50 dark:hover:bg-orange-950/30 px-4 py-2 rounded-xl text-sm font-bold transition-all duration-200 active:border-b-2 active:translate-y-[2px] shadow-sm [&_p]:m-0 [&_span]:m-0"
                                >
                                    <ReactMarkdown
                                        remarkPlugins={[remarkMath, remarkGfm]}
                                        rehypePlugins={[[rehypeKatex, { strict: false, trust: true }]]}
                                    >
                                        {pillText}
                                    </ReactMarkdown>
                                </motion.button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});

export default ChatMessageBubble;
